import { FC } from "react";
import { Box, Stack, Typography } from "@mui/material";
import LoadingButton from "src/components/LoadingButton";
import { RHFCheckbox, RHFTextField } from "src/components/hook-form";
import { useTranslation } from "react-i18next";
import { Divider } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { TFunction } from "i18next";
import { CompanyType } from "src/modules/company/types/Company";
import { useProfileUserPermissions } from "src/modules/profile/hooks/Profile/useUserPermissions";
import * as yup from "yup";

export const UpsertCompanyDetailsFormSchema = (t: TFunction, companyType: CompanyType | null) =>
    yup.object({
        name: yup.string().required(t("account.nameReq")), // Always required
        iWantInvoice: yup.boolean(),
        eipaUsername: yup
            .string()
            .email(t("configuration.validation.eipaUsername.email"))
            .notRequired()
            .nullable(),
        eipaPassword: yup.string().notRequired(),
        vatId: yup.string().when("iWantInvoice", {
            is: (iWantInvoice: boolean) =>
                companyType !== CompanyType.HOME ||
                (companyType === CompanyType.HOME && iWantInvoice === true),
            then: () =>
                yup
                    .string()
                    .required(t("account.vatIdReq"))
                    .test("isCorrect", t("account.vatNotValid"), function (value) {
                        // validate NIP
                        if (typeof value !== "string") return false;
                        // eslint-disable-next-line no-useless-escape
                        value = value.replace(/[\ \-]/gi, "");
                        const weight = [6, 5, 7, 2, 3, 4, 5, 6, 7];
                        let sum = 0;
                        const controlNumber = parseInt(value.substring(9, 10));
                        const weightCount = weight.length;
                        for (let i = 0; i < weightCount; i++) {
                            sum += parseInt(value.substr(i, 1)) * weight[i];
                        }
                        return sum % 11 === controlNumber;
                    }),
            otherwise: () => yup.string().nullable(),
        }),
        address: yup.mixed().when("iWantInvoice", {
            is: (iWantInvoice: boolean) =>
                companyType !== CompanyType.HOME ||
                (companyType === CompanyType.HOME && iWantInvoice === true),
            then: () =>
                yup
                    .object({
                        address1: yup.string().required(t("account.address1Req")),
                        address2: yup.string().nullable(),
                        city: yup.string().required(t("account.cityReq")),
                        postalCode: yup
                            .string()
                            .required(t("account.postalCodeReq"))
                            .matches(/^[0-9]{2}-[0-9]{3}$/, t("account.postalNotValid")),
                    })
                    .required(),
            otherwise: () => yup.mixed().nullable().notRequired(),
        }),
    });

const UpsertCompanyDetailsForm: FC = () => {
    const { t } = useTranslation();
    const { data: permissions } = useProfileUserPermissions();
    const companyType = permissions?.companyType || null;
    const { control } = useFormContext();

    const iWantInvoice = useWatch({ name: "iWantInvoice", control });
    const shouldShowInvoiceFields = companyType !== CompanyType.HOME || !!iWantInvoice;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {!iWantInvoice && companyType === CompanyType.HOME ? (
                    <>
                        <Typography variant="section">{t("account.accountDetails")}</Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "text.secondary",
                                maxWidth: { xs: "100%", md: "75%" },
                            }}
                        >
                            {t("account.accountDetailsHint")}
                        </Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="section">{t("account.companyDetails")}</Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", maxWidth: { xs: "100%", md: "75%" } }}
                        >
                            {t("account.companyDetailsHint")}
                        </Typography>
                    </>
                )}
            </Box>
            <Box
                sx={{
                    display: "grid",
                    alignItems: "end",
                    rowGap: 3,
                    columnGap: 3,
                    gridTemplateColumns: { xs: "repeat(1, 1fr)", md: "repeat(2, 1fr)" },
                }}
            >
                <RHFTextField
                    name="name"
                    label={shouldShowInvoiceFields ? t("account.name") : t("account.accountName")}
                    placeholder={t("account.namePl")}
                />
                <Box sx={{ display: { xs: "none", md: "block" } }} />
                {companyType === CompanyType.HOME && (
                    <>
                        <RHFCheckbox name={`iWantInvoice`} label={t("account.iWantInvoice")} />
                        <Box sx={{ display: { xs: "none", md: "block" } }} />
                    </>
                )}

                {shouldShowInvoiceFields && (
                    <>
                        <RHFTextField
                            name="address.address1"
                            label={t("account.address1")}
                            placeholder={t("account.address1Pl")}
                        />
                        <RHFTextField
                            name="address.address2"
                            placeholder={t("account.address2Pl")}
                        />
                        <RHFTextField
                            name="address.postalCode"
                            label={t("account.postcode")}
                            placeholder={t("account.postalCodePl")}
                        />
                        <RHFTextField
                            name="address.city"
                            label={t("account.city")}
                            placeholder={t("account.cityPl")}
                        />
                        <RHFTextField
                            name="vatId"
                            label={t("account.vatId")}
                            placeholder={t("account.vatIdPl")}
                        />
                    </>
                )}
            </Box>
            {companyType !== CompanyType.HOME && (
                <>
                    <Divider />
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Typography variant="section">{t("configuration.eipaAccount")}</Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", maxWidth: { xs: "100%", md: "75%" } }}
                        >
                            {t("configuration.eipaAccountHint")}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: "grid",
                            rowGap: 3,
                            columnGap: 3,
                            gridTemplateColumns: { xs: "repeat(1, 1fr)", md: "repeat(2, 1fr)" },
                        }}
                    >
                        <RHFTextField
                            name="eipaUsername"
                            label={t("configuration.eipaUsername")}
                            placeholder={t("configuration.eipaUsernamePl")}
                        />
                        <RHFTextField
                            name="eipaPassword"
                            placeholder={t("configuration.eipaPasswordPl")}
                            label={t("configuration.eipaPassword")}
                        />
                    </Box>
                </>
            )}
            <Divider />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <LoadingButton type="submit" size="large" variant="contained">
                    {t("account.saveBtn")}
                </LoadingButton>
            </Stack>
        </Box>
    );
};

export default UpsertCompanyDetailsForm;
